import { useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useFormPersistence } from './useFormPersistence';
import {
  parsePrice,
  validatePage1 as vPage1,
  validatePage2 as vPage2,
  validatePage3 as vPage3,
  validatePage4 as vPage4,
} from './formValidators';

function formatPrice(val) {
  const num = parseFloat(String(val ?? '').replace(/,/g, ''));
  return isNaN(num) ? '' : num.toLocaleString();
}

const INITIAL_UPLOAD = {
  images: [],
  front_deeds: [],
  back_deeds: [],
  youtube_url: '',
  vr_url: '',
  heading_post: '',
  detail_post: '',
  type_post: [],
  floor: null,
  area: null,
  unit_area: null,
  use_area: null,
  use_unit_area: null,
  bedroom: null,
  bathroom: null,
  rai: '0',
  ngan: '0',
  wah: '0',
  price: null,
  rent_price: null,
  sellout_price: null,
  owner_status: null,
  first_hand: null,
  furniture_included: null,
  phone_number: '',
  line_id: '',
  excursive_option: [],
  asset_location_latitude: null,
  asset_location_longitude: null,
};

export function usePropertyForm(config) {
  const [upload, setUpload] = useState(() => ({
    ...INITIAL_UPLOAD,
    use_unit_area: config.defaultUseAreaUnit,
  }));
  const [pageNumber, setPageNumber] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [detailLength, setDetailLength] = useState(0);
  const [formError, setFormError] = useState(null); // { field, message }
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { clearStorage } = useFormPersistence(
    config,
    upload,
    setUpload,
    (saved) => setDetailLength(saved.detail_post?.length ?? 0)
  );

  // ── Field setters ─────────────────────────────────────────────────────────

  const field = (name) => (e) => {
    setFormError(null);
    setUpload((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const setField = (name, value) => {
    setFormError(null);
    setUpload((prev) => ({ ...prev, [name]: value }));
  };

  const toggleArrayField = (name, value) => {
    setFormError(null);
    setUpload((prev) => {
      const arr = prev[name] ?? [];
      return {
        ...prev,
        [name]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  // ── Special handlers ──────────────────────────────────────────────────────

  const handleDetailPost = (e) => {
    const text = e.target.value;
    const MAX = 1800;
    setFormError(null);
    if (text.length <= MAX) {
      setUpload((prev) => ({ ...prev, detail_post: text }));
      setDetailLength(text.length);
    } else {
      const trimmed = text.slice(0, MAX);
      setUpload((prev) => ({ ...prev, detail_post: trimmed }));
      e.target.value = trimmed;
      setDetailLength(MAX);
      setFormError({ field: 'detail_post', message: `ป้อนข้อความได้สูงสุด ${MAX} ตัวอักษร` });
    }
  };

  const handlePrice = (name) => (e) => {
    setFormError(null);
    const raw = e.target.value.replace(/,/g, '');
    if (raw === '' || raw === '0') {
      setUpload((prev) => ({ ...prev, [name]: raw }));
      return;
    }
    const num = parseFloat(raw);
    setUpload((prev) => ({ ...prev, [name]: isNaN(num) ? raw : num.toLocaleString() }));
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validatePage1 = () => vPage1(upload);
  const validatePage2 = () => vPage2(upload, config);
  const validatePage3 = () => vPage3(upload, config);
  const validatePage4 = () => vPage4(upload);

  const PAGE_VALIDATORS = [null, validatePage1, validatePage2, validatePage3, validatePage4];

  // ── Navigation ────────────────────────────────────────────────────────────

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const goToPage = (target) => {
    if (target < pageNumber) { setPageNumber(target); scrollTop(); return; }
    for (let p = pageNumber; p < target; p++) {
      const result = PAGE_VALIDATORS[p]?.();
      if (!result?.ok) { setFormError(result); return; }
    }
    setFormError(null);
    setPageNumber(target);
    scrollTop();
  };

  const nextPage = () => {
    const result = PAGE_VALIDATORS[pageNumber]?.();
    if (result?.ok) {
      setFormError(null);
      setPageNumber((p) => p + 1);
      scrollTop();
    } else {
      setFormError(result);
    }
  };

  // ── Payload builder ───────────────────────────────────────────────────────

  const buildPayload = () => {
    const {
      images, front_deeds, back_deeds,
      youtube_url, vr_url,
      heading_post, detail_post, type_post,
      floor, area, unit_area, use_area, use_unit_area,
      bedroom, bathroom,
      rai, ngan, wah,
      price, rent_price, sellout_price,
      owner_status, first_hand, furniture_included,
      phone_number, line_id, excursive_option,
      asset_location_latitude, asset_location_longitude,
    } = upload;

    const payload = {
      images,
      frontdeeds: front_deeds,
      backdeeds: back_deeds,
      youtube_url,
      vr_url,
      heading_post,
      detail_post,
      type_post,
      owner_status,
      phone_number,
      line_id,
      excursive_option,
      asset_location_latitude,
      asset_location_longitude,
      sell_price:  parsePrice(price)        || null,
      rent_price:  parsePrice(rent_price)   || null,
    };

    if (config.floorPayloadKey)    payload[config.floorPayloadKey]    = floor;
    if (config.bedroomPayloadKey)  payload[config.bedroomPayloadKey]  = bedroom;
    if (config.bathroomPayloadKey) payload[config.bathroomPayloadKey] = bathroom;
    if (config.hasArea)    { payload.total_size  = area;     payload.total_unit   = unit_area;     }
    if (config.hasUseArea) { payload.living_size = use_area; payload.living_unit  = use_unit_area; }
    if (config.hasLandArea){ payload.rai = rai;  payload.ngan = ngan; payload.wah = wah; }
    if (config.hasFirstHand)  payload.first_hand         = first_hand;
    if (config.hasFurniture)  payload.furniture_included = furniture_included;
    if (config.hasSellOut)    payload.sellout_price       = parsePrice(sellout_price) || null;

    return payload;
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const result = validatePage4();
    if (!result?.ok) { setFormError(result); return; }

    const token = Cookies.get('jwt');
    if (!token) { setSubmitError('กรุณาเข้าสู่ระบบใหม่อีกครั้ง'); return; }

    setIsUploading(true);
    setSubmitError(null);
    try {
      await axios.post(
        `${process.env.REACT_APP_API}/asset/post/${config.endpoint}`,
        buildPayload(),
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
      );
      await clearStorage();
      setSubmitSuccess(true);
      setTimeout(() => window.location.replace('/home'), 1500);
    } catch (err) {
      const errData = err?.response?.data;
      setSubmitError(errData?.message || errData?.Error || errData?.error || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    } finally {
      setIsUploading(false);
    }
  };

  return {
    upload, setUpload,
    detailLength,
    pageNumber, setPageNumber,
    isUploading,
    formError,
    submitError,
    submitSuccess,
    field, setField, toggleArrayField,
    handleDetailPost, handlePrice, formatPrice,
    goToPage, nextPage,
    validatePage1, validatePage2, validatePage3, validatePage4,
    handleSubmit,
  };
}
